import { useEffect, useRef } from "react";

/**
 * Acquires a Screen Wake Lock while `active` is true, preventing the screen
 * from dimming or locking during an ongoing conversation.
 * Automatically re-acquires the lock if the page becomes visible again after
 * being hidden (the browser releases wake locks on visibility change).
 */
export default function useWakeLock(active: boolean) {
    const wakeLockRef = useRef<WakeLockSentinel | null>(null);

    const acquire = async () => {
        if (!("wakeLock" in navigator)) return;
        try {
            wakeLockRef.current = await navigator.wakeLock.request("screen");
        } catch {
            // Silently ignore — wake lock is best-effort
        }
    };

    const release = async () => {
        if (wakeLockRef.current) {
            await wakeLockRef.current.release().catch(() => {});
            wakeLockRef.current = null;
        }
    };

    useEffect(() => {
        if (active) {
            acquire();
        } else {
            release();
        }

        return () => {
            release();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [active]);

    // Re-acquire after the page becomes visible again (browser drops the lock on hide)
    useEffect(() => {
        if (!active) return;

        const handleVisibilityChange = () => {
            if (document.visibilityState === "visible") {
                acquire();
            }
        };

        document.addEventListener("visibilitychange", handleVisibilityChange);
        return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [active]);
}
